import { StateCreator } from 'zustand'
import { SortOrder } from '@moonpig/web-explore-types-graphql'
import {
  ApplicableFilter,
  IntegerFilter,
  FilterItem,
  FilterCategory,
} from '../../../services/types/services'
import { FilterAction, FilterSource } from '../../../types'
import {
  getPersistedFilters,
  getFiltersById,
  updateSelectedFilters,
  optimisticUpdatedFilterMapping,
  updateCategories,
  clearCategoryFilters,
  updateRoute,
  getIntegerFilterInput,
  clearAndUpdateFilters,
} from '../../helpers'
import { FiltersState, StateIntegerFilter } from './types'
import { ContextState } from '../context'
import { ProductsState } from '../products'
import { SharedState } from '../shared'
import { applySiblingFacetSelection } from '../../helpers/applySiblingFacetSelection'
import { isPageNavigationEnabled } from '../../../utils/getGalleryPageSize/getGalleryPageSize'

export const createFiltersSlice: StateCreator<
  FiltersState &
    Partial<ContextState> &
    Partial<SharedState> &
    Partial<ProductsState>
> = (set, get) => ({
  filtersMenuOpen: false,
  filterSelectionError: false,
  filters: [],
  quickFilterKeys: [],
  preAppliedFilters: [],
  integerFilters: [],
  selectedFilters: [],
  noResultsFilters: [],
  filteringEvent: undefined,
  selectedCategories: [],
  queryFilters: null,
  initialSortValue: SortOrder.POPULARITY,
  sortValue: SortOrder.POPULARITY,
  sortMenuOpen: false,
  optimisticRender: false,
  hasNbaFilters: false,
  hasRudeProducts: false,
  filterActionQueue: [],
  toggleFiltersMenu: source => {
    set(state => ({
      filtersMenuOpen: !state.filtersMenuOpen,
      selectedCategories: [],
      sortMenuOpen: source === 'sort',
    }))
  },
  toggleFiltersCategory: filtersCategory =>
    set(state => {
      const currentSelectedCategories = [...state.selectedCategories]

      const isCurrentCategory =
        filtersCategory.id ===
        currentSelectedCategories[currentSelectedCategories.length - 1]?.id
      if (isCurrentCategory) {
        currentSelectedCategories.pop()
      } else {
        currentSelectedCategories.push(filtersCategory)
      }
      return {
        selectedCategories: currentSelectedCategories,
      }
    }),
  optimisticallyApplyFilters: async filterActions => {
    const { selectedFilters, selectedCategories, filters } = get()

    let updatedSelectedFilters = selectedFilters
    let optimisticUpdatedFilters = filters

    filterActions.forEach(({ filter, select }) => {
      updatedSelectedFilters = updateSelectedFilters({
        selectedFilters: updatedSelectedFilters,
        newFilter: filter,
        select,
        allFilters: filters,
      })

      optimisticUpdatedFilters = optimisticUpdatedFilters.map(f =>
        optimisticUpdatedFilterMapping(f, filter, select),
      )
    })

    const updatedCategories = updateCategories(
      optimisticUpdatedFilters,
      selectedCategories.map(c => c.id),
    )

    set(() => {
      return {
        selectedFilters: updatedSelectedFilters,
        filters: optimisticUpdatedFilters,
        selectedCategories: updatedCategories,
        optimisticRender: true,
      }
    })
  },
  toggleFilter: async ({ filter, select, source }) => {
    const {
      selectedFilters,
      queryFilters,
      toggleLoading,
      preAppliedFilters,
      selectedCategories,
      pageContext: /* istanbul ignore next */ { router } = { router: null },
      optimisticallyApplyFilters,
      filters,
      filterActionQueue,
      searchContext,
      queryProducts,
      setFilterError,
      clearFilterError,
      integerFilters,
      galleryPageSize = 0,
    } = get()
    /* istanbul ignore else */
    if (queryFilters) {
      try {
        toggleLoading?.()
        clearFilterError()

        const newfilterAction = { filter, select, source }
        set(() => {
          return {
            filterActionQueue: [...filterActionQueue, newfilterAction],
          }
        })
        const filterActions = get().filterActionQueue

        const shouldOptimisticallyApplyFilters = filterActions.length <= 1

        if (shouldOptimisticallyApplyFilters) {
          optimisticallyApplyFilters(filterActions)
        }
        let filteredPreAppliedFilters = preAppliedFilters

        if (!select) {
          filteredPreAppliedFilters = preAppliedFilters.filter(
            x => x.key !== filter.directParentKey && x.key !== filter.id,
          )
        }

        const selectedFiltersWithPreappliedFilters =
          preAppliedFilters.length > 0
            ? applySiblingFacetSelection({
                selectedFilters,
                preAppliedFilters,
                filters,
                filter,
                select,
              })
            : selectedFilters

        const updatedSelectedFilters = filterActions.reduce(
          // eslint-disable-next-line @typescript-eslint/no-shadow
          (currentFilters, { filter, select }) => {
            return updateSelectedFilters({
              selectedFilters: currentFilters,
              newFilter: filter,
              select,
              allFilters: filters,
            })
          },
          selectedFiltersWithPreappliedFilters,
        )
        const {
          filters: updatedFilters,
          quickFilterKeys: updatedQuickFilterKeys,
        } = await queryFilters({
          filters: [
            ...updatedSelectedFilters.map(f => ({
              key: f.id,
              group: f.parent,
              userApplied: true,
            })),
            ...filteredPreAppliedFilters,
          ],
          integerFilter: getIntegerFilterInput(integerFilters),
        })

        const updatedCategories = updateCategories(
          updatedFilters,
          selectedCategories.map(c => c.id),
        )

        const removedSuggestion = preAppliedFilters.find(
          f =>
            (f.key === filter.id || f.key === filter.directParentKey) &&
            f.suggestion === true,
        )

        const hasSuggestionBeenRemoved =
          removedSuggestion &&
          removedSuggestion?.key === searchContext?.selectedSuggestion?.facetKey

        updateRoute({
          router,
          selectedFilters: updatedSelectedFilters,
          preAppliedFilters: filteredPreAppliedFilters,
          mostRecentSearchTerm: hasSuggestionBeenRemoved
            ? searchContext?.selectedSuggestion?.label ||
              /* istanbul ignore next */ searchContext?.searchTerm
            : '',
          usePageNavigation: isPageNavigationEnabled(galleryPageSize),
        })

        if (hasSuggestionBeenRemoved) {
          toggleLoading?.(false)
          return
        }

        const latestFilterActions = get().filterActionQueue

        const filteringEvent = {
          action: select
            ? FilterAction.Add
            : /* istanbul ignore next */ FilterAction.Remove,
          source,
          filter,
        }

        await queryProducts?.({
          newPage: 1,
          isLoadMore: false,
          isPrevious: false,
          filters: updatedSelectedFilters,
          filteringEvent,
          allFilters: updatedFilters,
          integerFilter: getIntegerFilterInput(integerFilters),
        })

        toggleLoading?.(false)

        set(() => {
          return {
            filters: updatedFilters,
            quickFilterKeys: updatedQuickFilterKeys ?? [],
            selectedFilters: updatedSelectedFilters,
            select,
            filteringEvent,
            selectedCategories: updatedCategories,
            preAppliedFilters: filteredPreAppliedFilters,
            optimisticRender: false,
            filterActionQueue: latestFilterActions.filter(
              latestAction =>
                !filterActions.some(
                  action =>
                    latestAction.filter === action.filter &&
                    latestAction.select === action.select &&
                    latestAction.source === action.source,
                ),
            ),
          }
        })
      } catch {
        toggleLoading?.(false)
        setFilterError()
        set(() => {
          return {
            selectedFilters,
            selectedCategories,
            filters,
            optimisticRender: false,
          }
        })
      }
    }
  },
  applyIntegerFilter: async ({ filter, filterId, value, source }) => {
    const {
      selectedFilters,
      queryFilters,
      toggleLoading,
      preAppliedFilters,
      selectedCategories,
      pageContext: /* istanbul ignore next */ { router } = { router: null },
      filters,
      queryProducts,
      setFilterError,
      clearFilterError,
      galleryPageSize = 0,
    } = get()

    const integerFilter = filter || {
      ...(getFiltersById({
        filterIds: [filterId],
        allFilters: filters,
      })[0] as IntegerFilter),
      selectedValue: value || /* istanbul ignore next */ null,
    }

    if (queryFilters) {
      try {
        toggleLoading?.()
        clearFilterError()

        const {
          filters: updatedFilters,
          noResultsFilters,
          quickFilterKeys: updatedQuickFilterKeys,
        } = await queryFilters({
          filters: [
            ...selectedFilters.map(f => ({
              key: f.id,
              group: f.parent,
              userApplied: true,
            })),
            ...preAppliedFilters,
          ],
          integerFilter: {
            [integerFilter.integerId.toLowerCase()]: {
              min: integerFilter.selectedValue,
              max: integerFilter.selectedValue,
            },
          },
        })

        if (noResultsFilters.length > 0) {
          toggleLoading?.(false)

          set(() => {
            return {
              filters: updatedFilters,
              quickFilterKeys: updatedQuickFilterKeys ?? [],
              selectedFilters,
              filteringEvent: {
                action: FilterAction.Add,
                source,
                filter: {
                  id: `${integerFilter.integerId}-${integerFilter.selectedValue}`,
                  parent: integerFilter.id,
                },
              },
              optimisticRender: false,
              noResultsFilters,
            }
          })
        } else {
          const updatedCategories = updateCategories(
            updatedFilters,
            selectedCategories.map(c => c.id),
          )
          const getIntegerFilters = getFiltersById({
            filterIds: [filter?.id || /* istanbul ignore next */ filterId],
            allFilters: updatedFilters,
          })

          const updatedIntegerFilter = getIntegerFilters?.[0] as
            | IntegerFilter
            | undefined

          const selected = updatedIntegerFilter
            ? [...selectedFilters, updatedIntegerFilter]
            : [...selectedFilters]

          updateRoute({
            router,
            selectedFilters: selected,
            preAppliedFilters,
            usePageNavigation: isPageNavigationEnabled(galleryPageSize),
          })

          const filteringEvent = {
            action: FilterAction.Add,
            source,
            filter: {
              id:
                updatedIntegerFilter?.integerKey ||
                /* istanbul ignore next */ '',
              parent:
                updatedIntegerFilter?.parent || /* istanbul ignore next */ '',
            },
          }

          await queryProducts?.({
            newPage: 1,
            isLoadMore: false,
            isPrevious: false,
            filters: selectedFilters,
            filteringEvent,
            integerFilter: updatedIntegerFilter
              ? {
                  [updatedIntegerFilter.integerId.toLowerCase()]: {
                    min: updatedIntegerFilter.selectedValue,
                    max: updatedIntegerFilter.selectedValue,
                  },
                }
              : undefined,
            allFilters: updatedFilters,
          })

          toggleLoading?.(false)

          set(() => {
            return {
              filters: updatedFilters,
              quickFilterKeys: updatedQuickFilterKeys ?? [],
              selectedFilters,
              filteringEvent,
              selectedCategories: updatedCategories,
              optimisticRender: false,
            }
          })
        }
      } catch {
        toggleLoading?.(false)
        setFilterError()
        set(() => {
          return {
            selectedFilters,
            selectedCategories,
            filters,
            optimisticRender: false,
          }
        })
      }
    }
  },
  onIntegerFilterChange: ({ integerFilterId, value }) => {
    const { integerFilters } = get()

    const updatedIntegerFilters = integerFilters.map(f =>
      f.integerId === integerFilterId
        ? { ...f, value }
        : /* istanbul ignore next */ f,
    )

    set(() => ({
      integerFilters: updatedIntegerFilters,
    }))
  },
  clearFilters: async ({ category, type, source }) => {
    const {
      queryFilters,
      toggleLoading,
      selectedFilters,
      pageContext: /* istanbul ignore next */ { router } = { router: null },
      filters,
      queryProducts,
      searchContext,
      initialSortValue,
      preAppliedFilters,
      integerFilters,
      galleryPageSize = 0,
    } = get()
    let updatedFilters: FilterItem[] = filters
    let updatedSelectedFilters: ApplicableFilter[] = selectedFilters
    let updatedIntegerFilters: StateIntegerFilter[] = integerFilters
    let updatedQuickFilterKeys: string[] = get().quickFilterKeys

    const clearCategoryOrAll =
      !category || category.__typename === 'FilterCategory'
    const filteringEvent = {
      action: category ? FilterAction.Clear : FilterAction.ClearAll,
      filter: category,
      source,
    }
    const preAppliedFiltersWithSuggestionsRemoved = preAppliedFilters.filter(
      f => !f.suggestion,
    )

    const hasSuggestionBeenRemoved =
      preAppliedFilters.length !==
      preAppliedFiltersWithSuggestionsRemoved.length

    /* istanbul ignore else */
    if (queryFilters) {
      if (!type || type === 'filters') {
        toggleLoading?.()
        const persistedFilters = getFiltersById({
          filterIds: getPersistedFilters(),
          allFilters: filters,
        })

        if (clearCategoryOrAll) {
          updatedSelectedFilters = category
            ? clearCategoryFilters(category as FilterCategory, [
                ...selectedFilters,
              ])
            : persistedFilters
        }

        const {
          filtersAfterClear,
          integerFiltersAfterClear,
          noResultsFiltersAfterClear,
          quickFilterKeysAfterClear,
        } = await clearAndUpdateFilters({
          preAppliedFilters: preAppliedFiltersWithSuggestionsRemoved,
          selectedFilters: updatedSelectedFilters,
          queryFilters,
          integerFilters,
          filter: category,
          router,
          searchContext,
          queryProducts,
          filteringEvent,
          usePageNavigation: isPageNavigationEnabled(galleryPageSize),
        })

        updatedFilters = filtersAfterClear
        updatedIntegerFilters = integerFiltersAfterClear
        updatedQuickFilterKeys = quickFilterKeysAfterClear ?? []

        if (clearCategoryOrAll && hasSuggestionBeenRemoved) {
          toggleLoading?.(false)
          return
        }

        set(() => ({ noResultsFilters: noResultsFiltersAfterClear }))
        toggleLoading?.(false)
      }

      if (type === 'sort') {
        await queryProducts?.({
          newPage: 1,
          isLoadMore: false,
          isPrevious: false,
          sortOrder: initialSortValue,
          filteringEvent,
        })

        toggleLoading?.(false)
      }

      set(state => {
        return {
          filters: updatedFilters,
          quickFilterKeys: updatedQuickFilterKeys,
          selectedFilters: updatedSelectedFilters,
          integerFilters: updatedIntegerFilters,
          filteringEvent,
          preAppliedFilters: preAppliedFiltersWithSuggestionsRemoved,
          selectedCategories: updateCategories(
            updatedFilters,
            state.selectedCategories.map(c => c.id),
          ),
          sortValue: category ? state.sortValue : state.initialSortValue,
        }
      })
    }
  },
  clearFiltersWhenNoResults: async ({ filter }) => {
    const {
      queryFilters,
      selectedFilters,
      pageContext: /* istanbul ignore next */ { router } = { router: null },
      filters,
      queryProducts,
      searchContext,
      preAppliedFilters,
      integerFilters,
      toggleLoading,
      galleryPageSize = 0,
    } = get()

    const updatedSelectedFilters =
      filter?.__typename === 'IntegerFilter'
        ? selectedFilters
        : selectedFilters.filter(f => f.id !== filter.id)
    const filteringEvent = {
      action: FilterAction.Clear,
      filter:
        filter.__typename === 'IntegerFilter'
          ? {
              id: filter.integerKey || /* istanbul ignore next */ '',
              parent: filter.parent,
            }
          : filter,
      source: FilterSource.NoSearchResults,
    }

    /* istanbul ignore else */
    if (queryFilters) {
      toggleLoading?.()

      const {
        filtersAfterClear,
        integerFiltersAfterClear,
        noResultsFiltersAfterClear,
        quickFilterKeysAfterClear,
      } = await clearAndUpdateFilters({
        preAppliedFilters,
        selectedFilters: updatedSelectedFilters,
        queryFilters,
        integerFilters,
        filter,
        router,
        searchContext,
        queryProducts,
        filteringEvent,
        usePageNavigation: isPageNavigationEnabled(galleryPageSize),
      })

      const updatedFilters =
        filtersAfterClear || /* istanbul ignore next */ filters

      set(state => ({
        filters: updatedFilters,
        quickFilterKeys: quickFilterKeysAfterClear ?? [],
        selectedFilters: updatedSelectedFilters,
        integerFilters: integerFiltersAfterClear,
        filteringEvent,
        preAppliedFilters,
        selectedCategories: updatedFilters
          ? updateCategories(
              updatedFilters,
              state.selectedCategories.map(c => c.id),
            )
          : /* istanbul ignore next */ state.selectedCategories,
        noResultsFilters: noResultsFiltersAfterClear,
      }))
      toggleLoading?.(false)
    }
  },
  setFilterError: () => set(() => ({ filterSelectionError: true })),
  clearFilterError: () => set(() => ({ filterSelectionError: false })),
  sortBy: async value => {
    const { queryProducts, toggleLoading } = get()

    toggleLoading?.()
    await queryProducts?.({
      newPage: 1,
      isLoadMore: false,
      isPrevious: false,
      sortOrder: value,
    })

    toggleLoading?.(false)
    set(() => ({ sortValue: value }))
  },
  resetFilteringEvent: () => set(() => ({ filteringEvent: undefined })),
})
